import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShieldCheck, 
  Search, 
  PlusCircle, 
  RefreshCcw, 
  Activity, 
  AlertCircle, 
  Clock, 
  ChevronRight, 
  Download,
  Filter,
  Zap,
  Lock,
  FileText,
  UploadCloud,
  X,
  Edit3,
  Trash2,
  CheckCircle2,
  Briefcase,
  FolderTree,
  Globe
} from 'lucide-react';
import { useRole } from '../hooks/useRole';
import { canEditComplianceRules, canLoadIMA, canViewSensitiveData } from '../utils/roleUtils';
import { cn } from '../lib/utils';

// ============== Animations ==============
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } }
};

const staggerContainer = {
  animate: { transition: { staggerChildren: 0.1 } }
};

// ============== Shared Components ==============
const Container = styled.div`
  min-height: calc(100vh - 64px);
  background: transparent;
  padding: 2rem;
`;

const GlassCard = styled.div`
  background: rgba(30, 41, 59, 0.5);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
`;

const StatCard: React.FC<{ label: string; value: string; icon: React.ReactNode; color: string; subText?: string }> = 
  ({ label, value, icon, color, subText }) => (
    <motion.div 
      variants={fadeInUp}
      className="glass-card p-6 border-l-4 relative overflow-hidden group"
      style={{ borderLeftColor: color }}
    >
      <div className="flex justify-between items-start">
        <div>
          <div className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">{label}</div>
          <div className="text-3xl font-bold text-white tracking-tight">{value}</div>
          {subText && <div className="text-[10px] text-slate-500 mt-2 font-medium">{subText}</div>}
        </div>
        <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center text-slate-400 group-hover:text-white transition-colors border border-white/5">
          {icon}
        </div>
      </div>
    </motion.div>
  );

type TabType = 'rules' | 'ima' | 'violations' | 'audit';

interface Portfolio {
  id: number;
  portfolioName: string;
}

interface PortfolioGroup {
  id: number;
  groupName: string;
}

interface ComplianceRule {
  id?: number;
  ruleName: string;
  ruleKey: string;
  description: string;
  ruleType: string;
  ruleScope: string;
  severity: string;
  breachAction: string;
  enabled: boolean;
  ruleValue?: any;
  thresholdValue?: string;
  portfolioId?: number;
  portfolioName?: string;
  portfolioGroupId?: number;
  portfolioGroupName?: string;
  source?: string;
}

interface EvaluatorField { name: string; type: string; label: string; required: boolean; defaultValue?: any; options?: string[] }
interface EvaluatorSpec { key: string; label: string; category: string; ruleType: string; description: string; aliases?: string[]; fields: EvaluatorField[] }

interface DashboardStats {
  totalRules: number;
  enabledRules: number;
  disabledRules: number;
  byScope: Record<string, number>;
  bySource: Record<string, number>;
}

const ComplianceRulesScreen: React.FC = () => {
  const { userRole, isAdmin } = useRole();

  const canEdit = canEditComplianceRules(userRole);
  const [activeTab, setActiveTab] = useState<TabType>('rules');
  const [rules, setRules] = useState<ComplianceRule[]>([]);
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [groups, setGroups] = useState<PortfolioGroup[]>([]);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterScope, setFilterScope] = useState<string>('ALL');
  const [filterSource, setFilterSource] = useState<string>('ALL');
  const [successMessage, setSuccessMessage] = useState('');
  const [error, setError] = useState<string | null>(null);

  // CLO indenture import: a deal's test schedule as CSV becomes rules on its collateral portfolio.
  // One "Import from document" surface: the type picks the parser and the rule catalogue (F-CB-010).
  const [importSource, setImportSource] = useState<'IMA' | 'CLO_INDENTURE'>('IMA');
  const cloImportOpen = activeTab === 'ima' && importSource === 'CLO_INDENTURE';
  const [cloImport, setCloImport] = useState({ portfolioId: '', portfolioGroupId: '', sourceDocument: '', taxonomy: '', ratingMethod: '', breachAction: 'BLOCK', csv: '', indexRatePct: '' });
  const [cloGroups, setCloGroups] = useState<any[]>([]);
  useEffect(() => {
    if (activeTab !== 'ima') return;
    axios.get('/api/portfolio-groups').then(res => setCloGroups(Array.isArray(res.data?.content) ? res.data.content : Array.isArray(res.data) ? res.data : [])).catch(() => setCloGroups([]));
  }, [activeTab]);
  // Indentures the document pipeline has parsed; "Read from indenture" fills the schedule from one.
  const [indenturesOnFile, setIndenturesOnFile] = useState<any[]>([]);
  const [indentureDealId, setIndentureDealId] = useState('');
  const [indentureRead, setIndentureRead] = useState<{ extracted: string[]; notExtracted: string[]; dealName: string } | null>(null);
  useEffect(() => {
    if (!cloImportOpen) return;
    axios.get('/api/compliance-rules/clo-indenture/on-file').then(res => {
      const list = Array.isArray(res.data) ? res.data : [];
      setIndenturesOnFile(list);
      const linked = list.find((i: any) => String(i.portfolioId) === String(cloImport.portfolioId));
      if (linked) setIndentureDealId(String(linked.dealId));
    }).catch(() => setIndenturesOnFile([]));
  }, [cloImportOpen, cloImport.portfolioId]);
  const readFromIndenture = async () => {
    if (!indentureDealId) return;
    try {
      const { data } = await axios.get('/api/compliance-rules/clo-indenture/from-indenture', { params: { dealId: indentureDealId } });
      setCloImport(prev => ({ ...prev, csv: data.csv, sourceDocument: prev.sourceDocument || `${data.indenture.dealName} indenture${data.indenture.indentureDate ? ', ' + data.indenture.indentureDate : ''}` }));
      setIndentureRead({ extracted: data.extracted, notExtracted: data.notExtracted, dealName: data.indenture.dealName });
    } catch (err: any) { setError(err.response?.data?.error || 'Could not read the indenture'); }
  };
  const [cloPreview, setCloPreview] = useState<any[] | null>(null);
  const [cloImporting, setCloImporting] = useState(false);
  const cloImportSample = 'test,threshold,units,clause\nOBLIGOR,0.02,fraction,§12.3(a)\nINDUSTRY,0.12,fraction,§12.3(b)\nWARF,3200,factor,§12.4\nWAS,350,bps,§12.5\nWAL,8,years,§12.6\nRATING_BUCKET,0.075,fraction,§12.7\nCASH,0,usd,§12.8\nELIGIBILITY,2029-11-01,,§12.1';
  const runCloImport = async (apply: boolean) => {
    if (!cloImport.portfolioId && !cloImport.portfolioGroupId) { setError('Choose the managed CLO portfolio or portfolio group'); return; }
    if (!cloImport.csv.trim()) { setError('Paste or upload the test schedule CSV'); return; }
    setCloImporting(true); setError(null);
    try {
      const { data } = await axios.post('/api/compliance-rules/clo-indenture/import', { ...cloImport, portfolioId: cloImport.portfolioId ? Number(cloImport.portfolioId) : null, portfolioGroupId: cloImport.portfolioGroupId ? Number(cloImport.portfolioGroupId) : null, apply });
      if (!data?.success) throw new Error(data?.error || 'Import failed');
      setCloPreview(Array.isArray(data.lines) ? data.lines : []);
      if (apply) {
        setSuccessMessage(data.message || 'CLO indenture rules imported');
        setTimeout(() => setSuccessMessage(''), 4000);
        setActiveTab('rules');
        setCloPreview(null);
        await fetchRules();
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Import failed');
    } finally {
      setCloImporting(false);
    }
  };

  // IMA parsing state
  const [imaText, setImaText] = useState('');
  const [imaDocName, setImaDocName] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [parseResult, setParseResult] = useState<any>(null);
  const [imaPortfolioId, setImaPortfolioId] = useState<number | ''>('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<ComplianceRule>>({
    ruleName: '',
    ruleKey: '',
    description: '',
    ruleType: 'LIMIT',
    ruleScope: 'FIRM',
    severity: 'MEDIUM',
    breachAction: 'BLOCK',
    enabled: true
  });

  // F-CMP-012: rules bind to governed evaluators (picker + typed fields), never a free-text key
  const [evaluators, setEvaluators] = useState<EvaluatorSpec[]>([]);
  const [showAdvancedJson, setShowAdvancedJson] = useState(false);
  const [classificationNodes, setClassificationNodes] = useState<{ code: string; name: string }[]>([]);
  useEffect(() => {
    axios.get('/api/compliance-rules/evaluators').then(r => setEvaluators(Array.isArray(r.data) ? r.data : [])).catch(() => setEvaluators([]));
    axios.get('/api/classification/nodes').then(r => {
      const raw = Array.isArray(r.data) ? r.data : (r.data?.content ?? r.data?.nodes ?? []);
      setClassificationNodes(raw.map((n: any) => ({ code: n.code ?? n.nodeCode ?? n.id, name: n.name ?? n.nodeName ?? n.label ?? '' })).filter((n: any) => n.code));
    }).catch(() => setClassificationNodes([]));
  }, []);
  const currentSpec = useMemo(() => {
    const k = String(formData.ruleKey || '');
    return evaluators.find(e => e.key.toLowerCase() === k.toLowerCase())
      || evaluators.find(e => (e.aliases || []).some(a => a.toUpperCase() === k.toUpperCase()));
  }, [evaluators, formData.ruleKey]);
  const isAliasKey = !!currentSpec && currentSpec.key.toLowerCase() !== String(formData.ruleKey || '').toLowerCase();
  const isLegacyKey = !!formData.ruleKey && evaluators.length > 0 && !currentSpec;
  const parsedValue: Record<string, any> = useMemo(() => {
    try {
      const v = typeof formData.ruleValue === 'string' ? JSON.parse(formData.ruleValue) : formData.ruleValue;
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        // legacy shapes the engine canonicalises at evaluation: {"threshold": n} / {"maxPercent": n}
        if (v.value === undefined && v.threshold !== undefined) return { ...v, value: v.threshold };
        if (v.value === undefined && v.maxPercent !== undefined) return { ...v, value: v.maxPercent };
        return v;
      }
      if (typeof v === 'number' || typeof v === 'string') return { value: v }; // bare scalar seeds
      return {};
    } catch { return {}; }
  }, [formData.ruleValue]);
  const setValueField = (name: string, value: any) => {
    setFormData(prev => {
      let cur: Record<string, any> = {};
      try { const v = typeof prev.ruleValue === 'string' ? JSON.parse(prev.ruleValue) : prev.ruleValue; if (v && typeof v === 'object' && !Array.isArray(v)) cur = v; } catch { /* start fresh */ }
      const next: Record<string, any> = { ...cur, [name]: value };
      if (name === 'value') { delete next.threshold; delete next.maxPercent; }
      return { ...prev, ruleValue: JSON.stringify(next, null, 2) };
    });
  };
  const selectEvaluator = (key: string) => {
    const spec = evaluators.find(e => e.key === key);
    if (!spec) { setFormData(prev => ({ ...prev, ruleKey: key })); return; }
    const defaults: Record<string, any> = {};
    spec.fields.forEach(f => { if (f.defaultValue !== undefined && f.defaultValue !== null) defaults[f.name] = f.defaultValue; });
    if (spec.fields.some(f => f.type === 'percent')) defaults.unit = 'percentage';
    setFormData(prev => ({
      ...prev,
      ruleKey: spec.key,
      ruleType: spec.ruleType,
      ruleName: prev.ruleName || spec.label,
      ruleValue: JSON.stringify(defaults, null, 2)
    }));
  };
  const evaluatorCategories = useMemo(() => Array.from(new Set(evaluators.map(e => e.category))), [evaluators]);

  const fetchRules = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/compliance-rules');
      setRules(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error fetching compliance rules:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get('/api/compliance-rules/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchMetadata = useCallback(async () => {
    try {
      const [portRes, groupRes] = await Promise.all([
        axios.get('/api/dimensions/portfolios'),
        axios.get('/api/portfolio-groups')
      ]);
      const portData = portRes.data?.content || portRes.data || [];
      const groupData = groupRes.data?.content || groupRes.data || [];
      setPortfolios(Array.isArray(portData) ? portData : []);
      setGroups(Array.isArray(groupData) ? groupData : []);
    } catch (error) {
      console.error('Error fetching metadata:', error);
    }
  }, []);

  useEffect(() => {
    fetchRules();
    fetchStats();
    fetchMetadata();
  }, [fetchRules, fetchStats, fetchMetadata]);

  const handleAdd = () => {
    setFormData({
      ruleName: '',
      ruleKey: '',
      description: '',
      ruleType: 'LIMIT',
      ruleScope: 'FIRM',
      severity: 'MEDIUM',
      breachAction: 'BLOCK',
      enabled: true,
      ruleValue: '{}'
    });
    setShowAdvancedJson(false);
    setIsEditing(false);
    setShowModal(true);
    setError(null);
  };

  const handleEdit = (rule: ComplianceRule) => {
    setFormData({ 
      ...rule,
      ruleValue: JSON.stringify((rule as any).ruleValue, null, 2)
    });
    setIsEditing(true);
    setShowModal(true);
    setError(null);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this compliance rule?')) return;
    
    try {
      await axios.delete(`/api/compliance-rules/${id}`);
      setSuccessMessage('Rule deleted successfully');
      fetchRules();
      fetchStats();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete rule');
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      let parsedValue = formData.ruleValue;
      try {
        if (typeof parsedValue === 'string') {
          parsedValue = JSON.parse(parsedValue);
        }
      } catch (e) {
        throw new Error('Invalid JSON format in Rule Value');
      }

      if (!formData.ruleKey) throw new Error('Choose what this rule checks (rule type)');
      if (formData.enabled && evaluators.length > 0 && !evaluators.some(ev => ev.key.toLowerCase() === String(formData.ruleKey).toLowerCase()) && !isAdmin) {
        throw new Error('This rule type has no governed evaluator; an enabled rule with it would block every trade in scope. Pick an evaluator or save it disabled.');
      }
      const payload = {
        ...formData,
        ruleValue: parsedValue
      };

      if (isEditing && formData.id) {
        await axios.put(`/api/compliance-rules/${formData.id}`, payload);
        setSuccessMessage('Rule updated successfully');
      } else {
        await axios.post('/api/compliance-rules', payload);
        setSuccessMessage('Rule created successfully');
      }
      setShowModal(false);
      fetchRules();
      fetchStats();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to save rule');
    } finally {
      setIsSaving(false);
    }
  };

  const handleParseIMA = async () => {
    if (!imaText.trim()) {
      alert('Please enter IMA document text');
      return;
    }
    
    setIsParsing(true);
    setParseResult(null);
    setError(null);
    
    try {
      // The IMA Parser AI functionality is consolidated into the Java backend (8081)
      // Reference: /docs/AI_ARCHITECTURE.md
      const response = await axios.post('/api/ima/parse', {
        documentText: imaText,
        documentName: imaDocName || 'Uploaded IMA Document'
      });
      
      if (response.data && response.data.success) {
        setParseResult(response.data);
        setSuccessMessage(`Successfully extracted ${response.data.extractedRulesCount || 0} rules!`);
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        throw new Error(response.data?.error || 'Failed to parse IMA');
      }
    } catch (err: any) {
      console.error('Error parsing IMA:', err);
      setError(err.response?.data?.error || err.message || 'Failed to connect to Java IMA Parser Service (Port 8081)');
      
      // Keep fallback for development if backend is not available
      if (process.env.NODE_ENV === 'development') {
        console.warn('Falling back to local extraction mock for development');
        setTimeout(() => {
          const fallbackRules = [
            {
              ruleName: "Max Position Size",
              ruleKey: "max_position_size",
              ruleType: "THRESHOLD",
              description: "No single security position shall exceed 5.0% of the total portfolio value.",
              ruleValue: { value: 5.0, unit: "percentage" },
              ruleScope: "PORTFOLIO",
              severity: "HARD",
              priority: 50,
              breachAction: "BLOCK"
            },
            {
              ruleName: "Minimum Credit Quality",
              ruleKey: "min_credit_quality",
              ruleType: "THRESHOLD",
              description: "The weighted average credit quality must be maintained at A- or higher.",
              ruleValue: { value: "A-", unit: "rating" },
              ruleScope: "PORTFOLIO",
              severity: "HARD",
              priority: 30,
              breachAction: "BLOCK"
            }
          ];
          setParseResult({
            success: true,
            extractedRulesCount: fallbackRules.length,
            rules: fallbackRules,
            documentAnalysis: "IMA document analyzed using local development mock."
          });
          setIsParsing(false);
        }, 1500);
      } else {
        setIsParsing(false);
      }
    } finally {
      // isParsing state handled in success/error branches
    }
  };

  const handleSaveRulesToOMS = async () => {
    if (!parseResult?.rules?.length) {
      alert('No rules to save');
      return;
    }
    
    if (!imaPortfolioId) {
      alert('Please select a portfolio to associate these rules with');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      // Associate rules with the selected portfolio and set scope to PORTFOLIO
      const rulesToSave = parseResult.rules.map((rule: any) => ({
        ...rule,
        portfolioId: imaPortfolioId,
        ruleScope: 'PORTFOLIO',
        source: 'IMA_PARSED'
      }));

      await axios.post('/api/compliance-rules/from-ima', {
        rules: rulesToSave,
        sourceDocument: imaDocName || 'Uploaded IMA Document',
        portfolioId: imaPortfolioId
      });
      setSuccessMessage(`Successfully saved ${parseResult.rules.length} rules as portfolio-specific mandates!`);
      fetchRules();
      fetchStats();
      setActiveTab('rules');
      setParseResult(null);
      setImaText('');
      setImaDocName('');
      setImaPortfolioId('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err: any) {
      console.error('Error saving rules:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save rules');
    } finally {
      setIsSaving(false);
    }
  };

  const loadSampleIMA = async () => {
    setIsLoading(true);
    // Local sample text as the Java backend doesn't have a dedicated sample endpoint
    // and port 5002 is reserved for the Rebalancing Agent in the new design.
    const sampleIMA = `INVESTMENT MANAGEMENT AGREEMENT

This AGREEMENT is made between AXIOM ASSET MANAGEMENT (the "Manager") and THE GLOBAL PENSION FUND (the "Client").

1. INVESTMENT OBJECTIVE
The Manager shall manage the Assets to achieve long-term capital appreciation with moderate risk.

2. INVESTMENT RESTRICTIONS
a) Diversification: No single security position shall exceed 5.0% of the total portfolio value at the time of purchase.
b) Credit Quality: The weighted average credit quality of the portfolio must be maintained at A- or higher as rated by S&P or Moody's.
c) Sector Limits: Total exposure to any single industry sector (GICS Level 1) shall not exceed 20% of the account value.
d) Prohibited Assets: The Manager shall not invest in securities issued by tobacco companies or manufacturers of cluster munitions.
e) Cash: A minimum of 2% and a maximum of 10% of the portfolio shall be held in cash or cash equivalents for liquidity.

3. REPORTING
The Manager will provide monthly performance attribution and compliance reports to the Client.`;
    
    setImaText(sampleIMA);
    setImaDocName("Global Pension Fund IMA 2024");
    if (portfolios.length > 0) {
      setImaPortfolioId(portfolios[0].id);
    }
    setSuccessMessage("Sample IMA loaded");
    setIsLoading(false);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const filteredRules = useMemo(() => {
    let result = rules;
    
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      result = result.filter(r => 
        r.ruleName?.toLowerCase().includes(s) || 
        r.description?.toLowerCase().includes(s) ||
        r.ruleKey?.toLowerCase().includes(s) ||
        r.portfolioName?.toLowerCase().includes(s) ||
        r.portfolioGroupName?.toLowerCase().includes(s)
      );
    }
    
    if (filterScope !== 'ALL') {
      result = result.filter(r => r.ruleScope === filterScope);
    }
    
    if (filterSource !== 'ALL') {
      result = result.filter(r => r.source === filterSource);
    }
    
    return result;
  }, [rules, searchTerm, filterScope, filterSource]);

  const formatRuleValue = (ruleValue: any): string => {
    if (typeof ruleValue === 'object' && ruleValue !== null) {
      if (ruleValue.value !== undefined) {
        const unit = ruleValue.unit || '';
        if (Array.isArray(ruleValue.value)) {
          return ruleValue.value.slice(0, 3).join(', ') + (ruleValue.value.length > 3 ? '...' : '');
        }
        return `${ruleValue.value} ${unit}`;
      }
      return JSON.stringify(ruleValue).slice(0, 30) + '...';
    }
    return String(ruleValue || 'N/A');
  };

  const cloIndentureForm = (
          <div className="w-full rounded-2xl border border-white/10 bg-slate-900 p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-white">Import CLO indenture tests</h2>
                <p className="mt-1 text-xs text-slate-400">One rule per test on the deal's collateral portfolio, measured on par at pre-clear and in the Portfolio Builder. Fractions become whole percent.</p>
              </div>
              <button onClick={() => setActiveTab('rules')} className="text-slate-500 hover:text-white text-sm">Back to rules</button>
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <label className="text-xs text-slate-400">Rules go on (a managed CLO portfolio, or a portfolio group sharing one rulebook)
                <select value={cloImport.portfolioGroupId ? `g:${cloImport.portfolioGroupId}` : cloImport.portfolioId ? `p:${cloImport.portfolioId}` : ''}
                  onChange={e => { const v = e.target.value; setCloImport({ ...cloImport, portfolioId: v.startsWith('p:') ? v.slice(2) : '', portfolioGroupId: v.startsWith('g:') ? v.slice(2) : '' }); }}
                  className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                  <option value="">Choose…</option>
                  <optgroup label="Portfolios">{portfolios.map(p => <option key={`p${p.id}`} value={`p:${p.id}`}>{p.portfolioName}</option>)}</optgroup>
                  <optgroup label="Portfolio groups">{cloGroups.map((g: any) => <option key={`g${g.id}`} value={`g:${g.id}`}>{g.groupName} ({g.portfolios?.length ?? 0} portfolios)</option>)}</optgroup>
                </select>
              </label>
              <label className="text-xs text-slate-400">Indenture (source document)
                <input value={cloImport.sourceDocument} onChange={e => setCloImport({ ...cloImport, sourceDocument: e.target.value })} placeholder="e.g. Cedar CLO A indenture, 2025-03-14"
                  className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
              </label>
              <label className="text-xs text-slate-400">Industry taxonomy code (blank = raw industry text)
                <input value={cloImport.taxonomy} onChange={e => setCloImport({ ...cloImport, taxonomy: e.target.value })} placeholder="e.g. MOODYS_INDUSTRY"
                  className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
              </label>
              <label className="text-xs text-slate-400">Rating-factor method (blank = Moody's table)
                <input value={cloImport.ratingMethod} onChange={e => setCloImport({ ...cloImport, ratingMethod: e.target.value })}
                  className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
              </label>
              <label className="text-xs text-slate-400">Index rate % for IC rows (e.g. SOFR 4.30)
                <input value={cloImport.indexRatePct} onChange={e => setCloImport({ ...cloImport, indexRatePct: e.target.value })} placeholder="required for IC A-1, IC B rows"
                  className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" />
              </label>
              <label className="text-xs text-slate-400">Breach action
                <select value={cloImport.breachAction} onChange={e => setCloImport({ ...cloImport, breachAction: e.target.value })}
                  className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                  <option value="BLOCK">BLOCK</option><option value="WARN">WARN</option><option value="NOTIFY">NOTIFY</option><option value="LOG">LOG</option>
                </select>
              </label>
              <label className="text-xs text-slate-400">CSV file
                <input type="file" accept=".csv,text/csv" onChange={e => { const f = e.target.files?.[0]; if (!f) return; f.text().then(text => setCloImport(prev => ({ ...prev, csv: text }))); }}
                  className="mt-1 w-full text-xs text-slate-300" />
              </label>
            </div>
            <div className="mt-4 rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-3">
              <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs text-slate-400 flex-1 min-w-[16rem]">Indenture on file (parsed by Document Processing)
                  <select value={indentureDealId} onChange={e => setIndentureDealId(e.target.value)} className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                    <option value="">{indenturesOnFile.length ? 'Choose…' : 'None parsed yet'}</option>
                    {indenturesOnFile.map((i: any) => <option key={i.dealId} value={i.dealId}>{i.dealName}{i.indentureDate ? ` · ${i.indentureDate}` : ''}{i.portfolioName ? ` · linked to ${i.portfolioName}` : ' · no portfolio linked'}</option>)}
                  </select>
                </label>
                <button type="button" onClick={readFromIndenture} disabled={!indentureDealId} className="px-4 py-2 rounded-lg bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white text-sm font-semibold">Read from indenture</button>
              </div>
              {(() => {
                const chosen = indenturesOnFile.find((i: any) => String(i.dealId) === indentureDealId);
                const mismatch = chosen && cloImport.portfolioId && String(chosen.portfolioId || '') !== String(cloImport.portfolioId);
                return mismatch ? <div className="mt-2 text-[11px] text-amber-300">This indenture belongs to {chosen.dealName}{chosen.portfolioName ? ` (${chosen.portfolioName})` : ' (no portfolio linked)'}, not to the portfolio chosen above. Its terms are a starting point only; check each test against the right indenture before creating rules.</div> : null;
              })()}
              <div className="mt-2 text-[11px] text-slate-500">
                {indentureRead
                  ? <>Read from {indentureRead.dealName}: <span className="text-slate-300">{indentureRead.extracted.join(', ')}</span>. Not in the parsed terms, type them below: <span className="text-amber-300">{indentureRead.notExtracted.join(', ')}</span>.</>
                  : 'A new indenture is uploaded in Document Processing (CLO indenture); its parsed terms then appear here. Obligor and industry limits are typed.'}
              </div>
            </div>
            <label className="mt-3 block text-xs text-slate-400">Test schedule (test, threshold, units, clause · OC and IC rows name the class: "OC A-1", "IC B")
              <textarea value={cloImport.csv} onChange={e => setCloImport({ ...cloImport, csv: e.target.value })} rows={6} placeholder={cloImportSample}
                className="mt-1 w-full bg-slate-950 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono text-white" />
            </label>
            {cloPreview && (
              <div className="mt-3 max-h-48 overflow-auto rounded-lg border border-white/10">
                <table className="w-full text-xs">
                  <thead className="bg-slate-950 text-slate-400"><tr><th className="px-2 py-1 text-left">Line</th><th className="px-2 py-1 text-left">Test</th><th className="px-2 py-1 text-left">Outcome</th><th className="px-2 py-1 text-left">Rule</th><th className="px-2 py-1 text-left">Value</th></tr></thead>
                  <tbody>
                    {cloPreview.map((l: any) => (
                      <tr key={l.number} className="border-t border-white/5 text-slate-300">
                        <td className="px-2 py-1">{l.number}</td><td className="px-2 py-1 whitespace-nowrap">{l.test}</td>
                        <td className={`px-2 py-1 ${l.problem ? 'text-amber-400' : 'text-emerald-400'}`}>{l.problem ? `${l.outcome}: ${l.problem}` : l.outcome}</td>
                        <td className="px-2 py-1 whitespace-nowrap">{l.ruleKey || '—'}</td>
                        <td className="px-2 py-1 font-mono max-w-[22rem] truncate" title={l.ruleValue ? JSON.stringify(l.ruleValue) : ''}>{l.ruleValue ? JSON.stringify(l.ruleValue) : ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="mt-4 flex items-center justify-end gap-3">
              <button onClick={() => setCloImport({ ...cloImport, csv: cloImportSample })} className="text-xs text-slate-400 hover:text-white">Use sample schedule</button>
              <button onClick={() => runCloImport(false)} disabled={cloImporting} className="px-4 py-2 rounded-lg border border-white/10 text-xs font-bold text-slate-200 hover:bg-white/5 disabled:opacity-50">Preview</button>
              <button onClick={() => runCloImport(true)} disabled={cloImporting || !cloPreview || cloPreview.every((l: any) => l.problem)} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-xs font-bold text-white disabled:opacity-50">Create rules</button>
            </div>
          </div>

  );

  return (
    <Container>
      <div className="max-w-[1800px] mx-auto space-y-8">
        <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-6">
          <div className="flex justify-between items-end">
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
                <ShieldCheck className="w-8 h-8 text-emerald-500" />
                Compliance Sentinel
              </h1>
              <p className="text-slate-500 font-medium mt-1">Institutional mandate enforcement and regulatory oversight engine</p>
            </div>
            <div className="flex gap-3">
              <div className="flex bg-slate-900 border border-white/10 p-1 rounded-xl">
                {['rules', 'violations', 'ima', 'audit'].map((tab) => (
                  <button 
                    key={tab}
                    onClick={() => setActiveTab(tab as any)}
                    className={cn(
                      "px-4 py-1.5 rounded-lg text-xs font-bold transition-all capitalize", 
                      activeTab === tab ? "bg-blue-600 text-white shadow-lg" : "text-slate-500 hover:text-slate-300"
                    )}
                  >
                    {tab === 'ima' ? 'Import from document' : tab}
                  </button>
                ))}
              </div>
              <button 
                onClick={fetchRules}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-white/10 rounded-xl text-sm font-bold text-slate-300 hover:text-white transition-all"
              >
                <RefreshCcw className={cn("w-4 h-4", isLoading && "animate-spin")} />
                Refresh
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard 
              label="Total active rules" 
              value={String(stats?.enabledRules ?? rules.filter(r => r.enabled).length)} 
              icon={<ShieldCheck className="w-5 h-5" />} 
              color="#10b981" 
              subText="Continuous Monitoring" 
            />
            <StatCard 
              label="Portfolio Specific" 
              value={String(stats?.byScope?.PORTFOLIO ?? rules.filter(r => r.ruleScope === 'PORTFOLIO').length)} 
              icon={<Briefcase className="w-5 h-5" />} 
              color="#3b82f6" 
              subText="Account Level Mandates" 
            />
            <StatCard 
              label="Group Rules" 
              value={String(stats?.byScope?.PORTFOLIO_GROUP ?? rules.filter(r => r.ruleScope === 'PORTFOLIO_GROUP' || r.ruleScope === 'GROUP').length)} 
              icon={<FolderTree className="w-5 h-5" />} 
              color="#f59e0b" 
              subText="Strategy Aggregates" 
            />
            <StatCard 
              label="Firm Level" 
              value={String(stats?.byScope?.FIRM ?? rules.filter(r => r.ruleScope === 'FIRM' || !r.ruleScope).length)} 
              icon={<Globe className="w-5 h-5" />} 
              color="#8b5cf6" 
              subText="Global Policy Engine" 
            />
          </div>
        </motion.div>

        {successMessage && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-xl flex items-center gap-3 text-emerald-400 font-bold"
          >
            <CheckCircle2 className="w-5 h-5" />
            {successMessage}
          </motion.div>
        )}

        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400 font-bold"
          >
            <AlertCircle className="w-5 h-5" />
            {error}
          </motion.div>
        )}

        <GlassCard className="flex flex-col h-[calc(100vh-400px)] min-h-[500px]">
          {activeTab === 'ima' ? (
            <div className="p-8 flex flex-col h-full overflow-y-auto custom-scrollbar">
              <div className="flex flex-wrap items-center gap-2 mb-5">
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mr-2">Document</span>
                {([['IMA', 'Investment management agreement'], ['CLO_INDENTURE', 'CLO indenture']] as const).map(([k, label]) => (
                  <button key={k} onClick={() => { setImportSource(k); setCloPreview(null); }}
                    className={cn('px-3.5 py-1.5 rounded-lg border text-xs font-semibold transition-colors', importSource === k ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200' : 'bg-slate-900/60 border-white/10 text-slate-400 hover:text-slate-200')}>{label}</button>
                ))}
                <span className="text-[11px] text-slate-500 ml-2">The document type picks the parser and the rule catalogue: an IMA yields market-value mandates; an indenture yields the par-based CLO tests and the note stack.</span>
              </div>
              {importSource === 'CLO_INDENTURE' ? null : (<>
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white flex items-center gap-2">
                    <UploadCloud className="w-6 h-6 text-emerald-500" />
                    AI-Powered IMA Parser
                  </h3>
                  <p className="text-slate-500 text-sm mt-1">Paste an investment management agreement to extract its mandates as rules</p>
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={loadSampleIMA}
                    className="px-4 py-2 bg-slate-800 border border-white/10 rounded-xl text-xs font-bold text-slate-300 hover:text-white transition-all"
                  >
                    Load Sample IMA
                  </button>
                  <button 
                    onClick={() => { setImaText(''); setImaDocName(''); setParseResult(null); setImaPortfolioId(''); }}
                    className="px-4 py-2 bg-slate-800 border border-white/10 rounded-xl text-xs font-bold text-slate-300 hover:text-white transition-all"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 flex-1 min-h-0">
                <div className="flex flex-col gap-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Document Name</label>
                      <input 
                        value={imaDocName}
                        onChange={e => setImaDocName(e.target.value)}
                        className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all"
                        placeholder="e.g. ABC Pension Fund IMA 2024"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Target Portfolio</label>
                      <select 
                        value={imaPortfolioId}
                        onChange={e => setImaPortfolioId(e.target.value ? parseInt(e.target.value) : '')}
                        className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                      >
                        <option value="">Select Portfolio...</option>
                        {portfolios.map(p => (
                          <option key={p.id} value={p.id}>{p.portfolioName}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex-1 flex flex-col space-y-2 min-h-[300px]">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Agreement Text</label>
                    <textarea 
                      value={imaText}
                      onChange={e => setImaText(e.target.value)}
                      className="flex-1 w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all font-mono resize-none"
                      placeholder="Paste the full text of the mandate or agreement here..."
                    />
                  </div>
                  <button 
                    onClick={handleParseIMA}
                    disabled={isParsing || !imaText.trim()}
                    className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white font-bold rounded-2xl transition-all shadow-xl shadow-emerald-600/20 flex items-center justify-center gap-3"
                  >
                    {isParsing ? (
                      <RefreshCcw className="w-5 h-5 animate-spin" />
                    ) : (
                      <Zap className="w-5 h-5" />
                    )}
                    {isParsing ? 'AI Parsing in Progress...' : 'Start AI Rule Extraction'}
                  </button>
                </div>

                <div className="flex flex-col bg-slate-950/50 rounded-2xl border border-white/5 overflow-hidden">
                  <div className="p-4 border-b border-white/5 bg-slate-900/30">
                    <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Extraction Results</h4>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
                    {!parseResult && !isParsing && (
                      <div className="h-full flex flex-col items-center justify-center text-center opacity-50 space-y-4">
                        <FileText className="w-12 h-12 text-slate-700" />
                        <p className="text-sm text-slate-500 italic max-w-[200px]">Results will appear here after parsing</p>
                      </div>
                    )}
                    
                    {isParsing && (
                      <div className="h-full flex flex-col items-center justify-center text-center space-y-4">
                        <motion.div 
                          animate={{ scale: [1, 1.1, 1] }} 
                          transition={{ repeat: Infinity, duration: 2 }}
                          className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center"
                        >
                          <Zap className="w-8 h-8 text-emerald-500" />
                        </motion.div>
                        <p className="text-sm text-emerald-400 font-bold">Agents are analyzing clauses...</p>
                      </div>
                    )}

                    {parseResult && (
                      <div className="space-y-4">
                        {parseResult.rules?.map((rule: any, idx: number) => (
                          <div key={idx} className="p-4 bg-slate-900 border border-white/5 rounded-xl space-y-2">
                            <div className="flex justify-between items-start">
                              <h5 className="font-bold text-white text-sm">{rule.ruleName}</h5>
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20 uppercase">
                                {rule.ruleType}
                              </span>
                            </div>
                            <p className="text-xs text-slate-500">{rule.description}</p>
                            <div className="pt-2 flex gap-2">
                              <code className="text-[10px] bg-slate-950 px-2 py-1 rounded text-emerald-400 border border-white/5">
                                {JSON.stringify(rule.ruleValue)}
                              </code>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {parseResult?.rules?.length > 0 && (
                    <div className="p-4 border-top border-white/5 bg-slate-900/30">
                      <button 
                        onClick={handleSaveRulesToOMS}
                        disabled={isSaving}
                        className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-xl transition-all shadow-xl shadow-blue-600/20 flex items-center justify-center gap-3"
                      >
                        {isSaving ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        Save All {parseResult.rules.length} Rules to OMS
                      </button>
                    </div>
                  )}
                </div>
              </div>
              </>)}
              {cloImportOpen && cloIndentureForm}
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-white/5 flex items-center justify-between bg-slate-900/30">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
                <input 
                  type="text" 
                  placeholder="Filter by rule, key, portfolio or group…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 pr-4 py-2 bg-slate-950/50 border border-white/10 rounded-xl text-sm text-white placeholder:text-slate-700 focus:outline-none focus:border-blue-500/50 transition-all w-64"
                />
              </div>
              <div className="flex bg-slate-950/50 border border-white/10 rounded-xl p-1">
                <select 
                  value={filterScope}
                  onChange={(e) => setFilterScope(e.target.value)}
                  className="bg-transparent text-xs font-bold text-slate-400 focus:outline-none px-2 py-1 appearance-none cursor-pointer hover:text-white transition-colors"
                >
                  <option value="ALL">ALL SCOPES</option>
                  <option value="FIRM">FIRM LEVEL</option>
                  <option value="PORTFOLIO_GROUP">GROUPS</option>
                  <option value="PORTFOLIO">PORTFOLIOS</option>
                </select>
              </div>
              <div className="flex bg-slate-950/50 border border-white/10 rounded-xl p-1">
                <select 
                  value={filterSource}
                  onChange={(e) => setFilterSource(e.target.value)}
                  className="bg-transparent text-xs font-bold text-slate-400 focus:outline-none px-2 py-1 appearance-none cursor-pointer hover:text-white transition-colors"
                >
                  <option value="ALL">ALL SOURCES</option>
                  <option value="MANUAL">MANUAL</option>
                  <option value="IMA_PARSED">IMA PARSED</option>
                  <option value="REGULATORY">REGULATORY</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {activeTab === 'rules' && (canLoadIMA(userRole) || canEdit) && (
                <button 
                  onClick={() => { setActiveTab('ima'); setCloPreview(null); }}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-xs font-bold text-white shadow-lg shadow-emerald-600/20 transition-all"
                  title="Turn a governing document (IMA, CLO indenture) into rules on a portfolio"
                >
                  <UploadCloud className="w-4 h-4" /> Import from document
                </button>
              )}
              {canEdit && activeTab === 'rules' && (
                <button 
                  onClick={handleAdd}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-lg text-xs font-bold text-white shadow-lg shadow-blue-600/20 hover:bg-blue-500 transition-all"
                >
                  <PlusCircle className="w-4 h-4" /> New Rule
                </button>
              )}
              <button className="p-2 text-slate-500 hover:text-white transition-colors"><Download className="w-4 h-4" /></button>
            </div>
          </div>

          <div className="flex-1 overflow-auto">
            <table className="w-full text-left">
              <thead className="sticky top-0 bg-slate-900 z-10">
                <tr className="text-[10px] font-bold text-slate-500 uppercase tracking-widest border-b border-white/5">
                  <th className="py-4 px-6">Rule Definition</th>
                  <th className="py-4 px-6">Scope & Assignment</th>
                  <th className="py-4 px-6">Threshold</th>
                  <th className="py-4 px-6">Severity / Action</th>
                  <th className="py-4 px-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {filteredRules.map((r, i) => (
                  <tr key={i} className="group hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-2 mb-0.5">
                        <div className={cn(
                          "w-1.5 h-1.5 rounded-full",
                          r.enabled ? "bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.5)]" : "bg-slate-600"
                        )} />
                        <div className="font-bold text-white">{r.ruleName}</div>
                        {r.source && r.source !== 'MANUAL' && (
                          <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                            {r.source}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 leading-relaxed max-w-md">{r.description}</div>
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex flex-col gap-1">
                        <span className={cn(
                          "w-fit px-2 py-0.5 rounded-md text-[9px] font-bold uppercase tracking-wider border border-white/5",
                          r.ruleScope === 'PORTFOLIO' ? "bg-blue-500/10 text-blue-400" : 
                          r.ruleScope === 'PORTFOLIO_GROUP' || r.ruleScope === 'GROUP' ? "bg-amber-500/10 text-amber-400" :
                          "bg-slate-800 text-slate-400"
                        )}>
                          {r.ruleScope || 'FIRM'}
                        </span>
                        {(r.portfolioName || r.portfolioGroupName) && (
                          <span className="text-[10px] font-medium text-slate-500 flex items-center gap-1">
                            {r.ruleScope === 'PORTFOLIO' ? <Briefcase className="w-2.5 h-2.5" /> : <FolderTree className="w-2.5 h-2.5" />}
                            {r.portfolioName || r.portfolioGroupName}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-6 font-mono text-slate-300 font-bold">{formatRuleValue(r.ruleValue)}</td>
                    <td className="py-4 px-6">
                      <div className="flex flex-col items-start gap-1">
                        <span className={cn(
                          "px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider",
                          r.severity === 'CRITICAL' ? "bg-red-500/10 text-red-400" :
                          r.severity === 'HIGH' ? "bg-orange-500/10 text-orange-400" :
                          r.severity === 'MEDIUM' ? "bg-amber-500/10 text-amber-400" :
                          "bg-slate-500/10 text-slate-400"
                        )}>
                          {r.severity || 'MEDIUM'}
                        </span>
                        <span className="px-2 py-0.5 rounded-md bg-slate-800 text-[9px] font-bold text-slate-400 uppercase tracking-wider">
                          {r.breachAction || 'BLOCK'}
                        </span>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <div className="flex justify-end gap-2">
                        {canEdit && (
                          <>
                            <button 
                              onClick={() => handleEdit(r)}
                              className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-all border border-blue-500/20" 
                              title="Edit"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                            <button 
                              onClick={() => r.id && handleDelete(r.id)}
                              className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all border border-red-500/20" 
                              title="Delete"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                        <button className="p-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-white transition-all border border-white/5">
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredRules.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-24 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <ShieldCheck className="w-12 h-12 text-slate-800" />
                        <div className="text-slate-500 italic">No compliance rules matched your search</div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </GlassCard>
  </div>

  <AnimatePresence>
    {showModal && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-md"
              onClick={() => setShowModal(false)}
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl bg-slate-900 border border-white/10 rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="p-8 border-b border-white/5 bg-slate-950/30 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-white tracking-tight">
                    {isEditing ? 'Edit Compliance Rule' : 'Add New Rule'}
                  </h2>
                  <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-1">Regulatory Mandate Engine</p>
                </div>
                <button onClick={() => setShowModal(false)} className="p-2 text-slate-500 hover:text-white transition-colors">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Rule Name</label>
                    <input 
                      value={formData.ruleName || ''}
                      onChange={e => setFormData({...formData, ruleName: e.target.value})}
                      className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all"
                      placeholder="e.g. Max Sector Weight"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Rule Type (evaluator)</label>
                    <select
                      value={isAliasKey ? currentSpec!.key : (formData.ruleKey || '')}
                      onChange={e => selectEvaluator(e.target.value)}
                      className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                    >
                      <option value="">Select what this rule checks…</option>
                      {isLegacyKey && <option value={formData.ruleKey}>Legacy key: {formData.ruleKey}</option>}
                      {evaluatorCategories.map(cat => (
                        <optgroup key={cat} label={cat}>
                          {evaluators.filter(e => e.category === cat).map(e => <option key={e.key} value={e.key}>{e.label}</option>)}
                        </optgroup>
                      ))}
                    </select>
                    {currentSpec && <div className="text-xs text-slate-500 px-1">{currentSpec.description}</div>}
                    {isAliasKey && <div className="text-xs text-slate-500 px-1">Stored under legacy key <span className="font-mono">{formData.ruleKey}</span>, which the engine maps to this evaluator.</div>}
                    {isLegacyKey && <div className="text-xs text-amber-400 px-1">This rule uses a legacy key (an alias the engine still understands, or one with no evaluator). Pick an evaluator to modernise it.</div>}
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Description</label>
                  <textarea 
                    value={formData.description || ''}
                    onChange={e => setFormData({...formData, description: e.target.value})}
                    className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all min-h-[80px]"
                    placeholder="Describe the compliance requirement..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Rule Scope</label>
                    <select 
                      value={formData.ruleScope || 'FIRM'}
                      onChange={e => setFormData({...formData, ruleScope: e.target.value, portfolioId: undefined, portfolioGroupId: undefined})}
                      className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                    >
                      <option value="FIRM">GLOBAL (FIRM)</option>
                      <option value="PORTFOLIO">PORTFOLIO SPECIFIC</option>
                      <option value="PORTFOLIO_GROUP">PORTFOLIO GROUP</option>
                    </select>
                  </div>
                  {formData.ruleScope === 'PORTFOLIO' && (
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Assign Portfolio</label>
                      <select 
                        value={formData.portfolioId || ''}
                        onChange={e => setFormData({...formData, portfolioId: parseInt(e.target.value)})}
                        className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                      >
                        <option value="">Select Portfolio</option>
                        {portfolios.map(p => <option key={p.id} value={p.id}>{p.portfolioName}</option>)}
                      </select>
                    </div>
                  )}
                  {formData.ruleScope === 'PORTFOLIO_GROUP' && (
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Assign Group</label>
                      <select 
                        value={formData.portfolioGroupId || ''}
                        onChange={e => setFormData({...formData, portfolioGroupId: parseInt(e.target.value)})}
                        className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                      >
                        <option value="">Select Group</option>
                        {groups.map(g => <option key={g.id} value={g.id}>{g.groupName}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                {currentSpec && currentSpec.fields.length > 0 && (
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Limit</label>
                    <div className="grid grid-cols-2 gap-6">
                      {currentSpec.fields.map(f => {
                        const val = parsedValue[f.name];
                        const cls = "w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all";
                        return (
                          <div key={f.name} className="space-y-1">
                            <div className="text-xs text-slate-400">{f.label}{f.required && <span className="text-red-400"> *</span>}</div>
                            {(f.type === 'percent' || f.type === 'number' || f.type === 'amount' || f.type === 'integer') && (
                              <div className="relative">
                                <input type="number" step={f.type === 'integer' ? 1 : 'any'} value={val ?? ''} className={cls}
                                  onChange={e => setValueField(f.name, e.target.value === '' ? null : (f.type === 'integer' ? parseInt(e.target.value, 10) : parseFloat(e.target.value)))} />
                                {f.type === 'percent' && <span className="absolute right-4 top-3 text-xs text-slate-500">%</span>}
                              </div>
                            )}
                            {f.type === 'choice' && (
                              <select value={val ?? ''} onChange={e => setValueField(f.name, e.target.value)} className={cls + " appearance-none"}>
                                <option value="">Select…</option>
                                {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                              </select>
                            )}
                            {f.type === 'boolean' && (
                              <label className="flex items-center gap-2 text-sm text-slate-300 py-3">
                                <input type="checkbox" checked={!!val} onChange={e => setValueField(f.name, e.target.checked)} /> {f.label}
                              </label>
                            )}
                            {f.type === 'list' && (
                              <textarea value={Array.isArray(val) ? val.join(', ') : (val ?? '')} className={cls + " min-h-[60px] font-mono"} placeholder="comma-separated"
                                onChange={e => setValueField(f.name, e.target.value.split(',').map(x => x.trim()).filter(Boolean))} />
                            )}
                            {f.type === 'classification_node' && (
                              <>
                                <input list="classification-node-list" value={val ?? ''} className={cls + " font-mono"} placeholder="e.g. STRUCTURED_CREDIT"
                                  onChange={e => setValueField(f.name, e.target.value.toUpperCase())} />
                                <datalist id="classification-node-list">
                                  {classificationNodes.map(n => <option key={n.code} value={n.code}>{n.name}</option>)}
                                </datalist>
                              </>
                            )}
                            {f.type === 'text' && (
                              <input value={val ?? ''} className={cls} onChange={e => setValueField(f.name, e.target.value)} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {currentSpec && currentSpec.fields.length === 0 && (
                  <div className="text-xs text-slate-500">This check needs no limit value.</div>
                )}

                {(isAdmin || isLegacyKey) && (
                  <div className="space-y-2">
                    <button type="button" onClick={() => setShowAdvancedJson(v => !v)} className="text-[10px] font-bold text-slate-500 uppercase tracking-widest hover:text-slate-300">
                      {showAdvancedJson ? '▾' : '▸'} Advanced: rule logic (JSON)
                    </button>
                    {(showAdvancedJson || isLegacyKey) && (
                      <textarea
                        value={formData.ruleValue || ''}
                        onChange={e => setFormData({...formData, ruleValue: e.target.value})}
                        className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all font-mono min-h-[100px]"
                        placeholder='{"value": 15.0, "unit": "percentage"}'
                      />
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Severity</label>
                    <select 
                      value={formData.severity || 'MEDIUM'}
                      onChange={e => setFormData({...formData, severity: e.target.value})}
                      className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                    >
                      <option value="CRITICAL">CRITICAL</option>
                      <option value="HIGH">HIGH</option>
                      <option value="MEDIUM">MEDIUM</option>
                      <option value="LOW">LOW</option>
                      <option value="INFO">INFO</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Breach Action</label>
                    <select
                      value={formData.breachAction || 'BLOCK'}
                      onChange={e => setFormData({...formData, breachAction: e.target.value})}
                      className="w-full bg-slate-950 border border-white/10 rounded-2xl px-4 py-3 text-sm text-white focus:outline-none focus:border-blue-500/50 transition-all appearance-none"
                    >
                      <option value="BLOCK">BLOCK</option>
                      <option value="WARN">WARN</option>
                      <option value="NOTIFY">NOTIFY</option>
                      <option value="LOG">LOG</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-4 bg-slate-950/50 border border-white/5 rounded-2xl">
                  <input 
                    type="checkbox"
                    id="rule-enabled"
                    checked={formData.enabled}
                    onChange={e => setFormData({...formData, enabled: e.target.checked})}
                    className="w-4 h-4 rounded border-white/10 bg-slate-900 text-blue-600 focus:ring-blue-500/50"
                  />
                  <label htmlFor="rule-enabled" className="text-sm font-bold text-white">Rule is Active</label>
                </div>

                <div className="flex gap-4 pt-4">
                  <button 
                    onClick={() => setShowModal(false)}
                    className="flex-1 py-4 bg-slate-800 hover:bg-slate-700 text-white font-bold rounded-2xl transition-all"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleSave}
                    disabled={isSaving || !formData.ruleName || !formData.ruleKey}
                    className="flex-1 py-4 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold rounded-2xl transition-all shadow-xl shadow-blue-600/20 flex items-center justify-center gap-3"
                  >
                    {isSaving ? (
                      <RefreshCcw className="w-5 h-5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-5 h-5" />
                    )}
                    {isEditing ? 'Update Rule' : 'Create Rule'}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </Container>
  );
};

export default ComplianceRulesScreen;
